import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  format, startOfMonth, endOfMonth,
  startOfWeek, endOfWeek, eachDayOfInterval,
  isSameMonth, isSameDay, isToday, addMonths, subMonths,
} from "date-fns";
import {
  ChevronLeft, ChevronRight, Truck, Phone, User,
  ArrowRight, X, ExternalLink, Package,
} from "lucide-react";
import { toast } from "sonner";
import { buildCityRegionMap, regionChipStyle, regionForItem } from "@/lib/regionColors";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUS_PILL = {
  "Active":    "bg-blue-100 text-blue-700",
  "Scheduled": "bg-indigo-100 text-indigo-700",
  "Completed": "bg-green-100 text-green-700",
  "Cancelled": "bg-red-100 text-red-700",
  "On Hold":   "bg-yellow-100 text-yellow-700",
};

/* ── Main page ─────────────────────────────────────────────────────────── */
export default function MovementCalendarPage() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [cityRegionMap, setCityRegionMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/confirmed-leads`, { withCredentials: true }),
      axios.get(`${API}/cities`, { withCredentials: true }),
    ])
      .then(([bookingsResponse, citiesResponse]) => {
        setBookings(bookingsResponse.data);
        setCityRegionMap(buildCityRegionMap(citiesResponse.data));
      })
      .catch(() => toast.error("Failed to load confirmed leads."))
      .finally(() => setLoading(false));
  }, []);

  /* Build date → bookings map using shifting_date */
  const movementMap = useMemo(() => {
    const map = {};
    bookings.forEach(b => {
      if (!b.shifting_date) return;
      if (!map[b.shifting_date]) map[b.shifting_date] = [];
      map[b.shifting_date].push(b);
    });
    return map;
  }, [bookings]);

  const todayStr = format(new Date(), "yyyy-MM-dd");

  /* Stats */
  const totalThisMonth = Object.entries(movementMap)
    .filter(([d]) => d.startsWith(format(currentMonth, "yyyy-MM")))
    .reduce((s, [, arr]) => s + arr.length, 0);

  const upcoming = Object.entries(movementMap)
    .filter(([d]) => d >= todayStr)
    .reduce((s, [, arr]) => s + arr.length, 0);

  const todayCount = (movementMap[todayStr] || []).length;

  /* Calendar grid */
  const monthStart = startOfMonth(currentMonth);
  const monthEnd   = endOfMonth(currentMonth);
  const calDays    = eachDayOfInterval({
    start: startOfWeek(monthStart, { weekStartsOn: 1 }),
    end:   endOfWeek(monthEnd,   { weekStartsOn: 1 }),
  });

  const selectedDayStr = selectedDay ? format(selectedDay, "yyyy-MM-dd") : null;
  const selectedItems  = selectedDayStr ? (movementMap[selectedDayStr] || []) : [];
  const calendarRegions = useMemo(
    () => Array.from(new Map(Object.values(cityRegionMap).map(region => [region.id, region])).values()),
    [cityRegionMap],
  );

  return (
    <div className="h-full flex flex-col bg-[#F8FAFC]">

      {/* ── Page header ── */}
      <div className="bg-[#1B2A6B] px-6 py-5 flex items-center justify-between shrink-0">
        <div>
          <h1 className="text-white font-black tracking-tighter text-2xl flex items-center gap-2">
            <Truck size={22} className="text-[#FFD700]" />
            Pick-ups &amp; Delivery Calendar
          </h1>
          <p className="text-white/60 text-xs mt-0.5">Read-only view of confirmed pick-up and delivery schedules</p>
        </div>
        <div className="flex items-center gap-7">
          <Stat label="This Month" value={totalThisMonth} color="text-[#FFD700]" />
          <div className="w-px h-8 bg-white/20" />
          <Stat label="Upcoming"   value={upcoming}       color="text-sky-400" />
          <div className="w-px h-8 bg-white/20" />
          <Stat label="Today"      value={todayCount}     color="text-green-400" />
        </div>
      </div>

      {/* ── Body ── */}
      <div className="flex flex-1 overflow-hidden">

        {/* ── Left: calendar grid ── */}
        <div className="flex-1 p-6 overflow-y-auto">
          {/* Month nav */}
          <div className="flex items-center justify-between mb-5">
            <button
              onClick={() => setCurrentMonth(m => subMonths(m, 1))}
              className="p-2 rounded-sm hover:bg-[#E2E8F0] transition-colors"
              data-testid="prev-month-btn"
            >
              <ChevronLeft size={16} className="text-[#475569]" />
            </button>
            <h2 className="text-sm font-black uppercase tracking-widest text-[#0F172A]">
              {format(currentMonth, "MMMM yyyy")}
            </h2>
            <button
              onClick={() => setCurrentMonth(m => addMonths(m, 1))}
              className="p-2 rounded-sm hover:bg-[#E2E8F0] transition-colors"
              data-testid="next-month-btn"
            >
              <ChevronRight size={16} className="text-[#475569]" />
            </button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 mb-1">
            {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map(d => (
              <div key={d} className="text-center text-[10px] font-bold uppercase tracking-widest text-[#94A3B8] py-2">
                {d}
              </div>
            ))}
          </div>

          {/* Day cells */}
          {loading ? (
            <div className="flex items-center justify-center h-64 text-sm text-[#94A3B8]">Loading…</div>
          ) : (
            <div className="grid grid-cols-7 gap-px bg-[#E2E8F0]">
              {calDays.map(day => {
                const dayStr   = format(day, "yyyy-MM-dd");
                const dayItems = movementMap[dayStr] || [];
                const inMonth  = isSameMonth(day, currentMonth);
                const todayFlag = isToday(day);
                const selFlag  = selectedDay && isSameDay(day, selectedDay);

                return (
                  <button
                    key={dayStr}
                    data-testid={`cal-day-${dayStr}`}
                    onClick={() => setSelectedDay(selFlag ? null : day)}
                    className={`
                      min-h-[88px] p-2 text-left transition-colors
                      ${!inMonth ? "bg-[#F8FAFC]" : selFlag ? "bg-[#FFF8E7]" : "bg-white"}
                      ${selFlag ? "ring-2 ring-inset ring-[#FFD700]" : ""}
                      hover:bg-[#FFFBEB]
                    `}
                  >
                    {/* Day number */}
                    <span className={`
                      inline-flex items-center justify-center w-6 h-6 text-xs font-bold rounded-full mb-1
                      ${todayFlag ? "bg-[#1B2A6B] text-[#FFD700]" : inMonth ? "text-[#0F172A]" : "text-[#CBD5E1]"}
                    `}>
                      {format(day, "d")}
                    </span>

                    {/* Booking chips */}
                    {inMonth && dayItems.length > 0 && (
                      <div className="space-y-0.5">
                        {dayItems.slice(0, 2).map((b, i) => (
                          <div
                            key={i}
                            style={regionChipStyle(regionForItem(cityRegionMap, b))}
                            className={`text-[9px] font-semibold px-1 py-0.5 truncate leading-tight ${regionForItem(cityRegionMap, b) ? "" : "bg-[#FFF3CD] text-[#92400E]"}`}
                          >
                            {b.customer_name}
                          </div>
                        ))}
                        {dayItems.length > 2 && (
                          <p className="text-[9px] text-[#94A3B8] font-semibold pl-1">
                            +{dayItems.length - 2} more
                          </p>
                        )}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Legend */}
          <div className="flex items-center gap-x-5 gap-y-2 mt-4 flex-wrap">
            {calendarRegions.map(region => (
              <div key={region.id} className="flex items-center gap-1.5 text-[10px] text-[#475569] font-medium">
                <span className="w-3 h-3 border border-black/10" style={{ backgroundColor: region.color }} />
                {region.name}
              </div>
            ))}
            <div className="flex items-center gap-1.5 text-[10px] text-[#475569] font-medium"><span className="w-3 h-3 bg-[#FFFBEB] border border-[#FFD700]" />Today</div>
          </div>
        </div>

        {/* ── Right: day detail panel ── */}
        <div className="w-[380px] border-l border-[#E2E8F0] bg-white flex flex-col overflow-hidden">
          {!selectedDay ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center px-8 gap-3">
              <Truck size={40} className="text-[#E2E8F0]" />
              <p className="text-sm font-semibold text-[#475569]">Click a day to see movements</p>
              <p className="text-xs text-[#94A3B8]">Days with amber chips have confirmed lead shifting dates</p>
            </div>
          ) : (
            <>
              {/* Panel header */}
              <div className="px-5 py-4 border-b border-[#E2E8F0] bg-[#F9FAFB] shrink-0 flex items-start justify-between">
                <div>
                  <p className="text-sm font-black text-[#0F172A]">
                    {format(selectedDay, "EEEE, d MMMM yyyy")}
                  </p>
                  <p className="text-[10px] text-[#94A3B8] mt-0.5">
                    {selectedItems.length === 0
                      ? "No movements scheduled"
                      : `${selectedItems.length} movement${selectedItems.length !== 1 ? "s" : ""} scheduled`}
                  </p>
                </div>
                <button onClick={() => setSelectedDay(null)} className="text-[#94A3B8] hover:text-[#475569] p-1 mt-0.5">
                  <X size={15} />
                </button>
              </div>

              {/* Booking cards */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {selectedItems.length === 0 ? (
                  <div className="text-center py-10">
                    <p className="text-sm text-[#94A3B8]">No movements on this day</p>
                  </div>
                ) : (
                  selectedItems.map(booking => (
                    <MovementCard
                      key={booking.id}
                      booking={booking}
                      region={regionForItem(cityRegionMap, booking)}
                      onNavigate={() => navigate(`/confirmed-leads/${booking.id}`)}
                    />
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Stat badge ── */
function Stat({ label, value, color }) {
  return (
    <div className="text-center">
      <p className={`text-2xl font-black leading-none ${color}`}>{value}</p>
      <p className="text-white/60 text-[10px] uppercase tracking-widest mt-0.5">{label}</p>
    </div>
  );
}

/* ── Single movement card ── */
function MovementCard({ booking, region, onNavigate }) {
  return (
    <div className="border border-[#E2E8F0] hover:border-[#FFD700] transition-colors" style={region ? { borderTop: `3px solid ${region.color}` } : undefined}>
      <div className="p-3">
        {/* Name + status */}
        <div className="flex items-start justify-between gap-2 mb-2.5">
          <button
            data-testid={`movement-booking-link-${booking.id}`}
            onClick={onNavigate}
            className="text-sm font-bold text-[#1B2A6B] hover:underline underline-offset-2 text-left flex items-center gap-1"
          >
            {booking.customer_name}
            <ExternalLink size={11} className="text-[#94A3B8] shrink-0" />
          </button>
          <span className={`text-[10px] font-bold px-1.5 py-0.5 whitespace-nowrap ${STATUS_PILL[booking.status] || "bg-gray-100 text-gray-700"}`}>
            {booking.status}
          </span>
        </div>

        {/* Route */}
        {(booking.moving_from || booking.moving_to) && (
          <div className="flex items-center gap-1.5 bg-[#F9FAFB] border border-[#E2E8F0] px-2.5 py-2 mb-2">
            <span className="text-xs font-semibold text-[#0F172A] truncate">{booking.moving_from || "—"}</span>
            <ArrowRight size={12} className="text-[#94A3B8] shrink-0" />
            <span className="text-xs font-semibold text-[#0F172A] truncate">{booking.moving_to || "—"}</span>
          </div>
        )}

        {/* Meta */}
        <div className="space-y-1.5">
          {booking.phone && (
            <div className="flex items-center gap-1.5 text-xs text-[#475569]">
              <Phone size={11} className="text-[#94A3B8] shrink-0" />
              <span>{booking.phone}</span>
            </div>
          )}
          {booking.assigned_to_name && (
            <div className="flex items-center gap-1.5 text-xs text-[#475569]">
              <User size={11} className="text-[#94A3B8] shrink-0" />
              <span>{booking.assigned_to_name}</span>
            </div>
          )}
          {region && (
            <div className="flex items-center gap-1.5 text-xs text-[#475569]">
              <span className="w-2.5 h-2.5 rounded-full border border-black/10" style={{ backgroundColor: region.color }} />
              <span>{region.name}</span>
            </div>
          )}
          {booking.load_size && (
            <div className="flex items-center gap-1.5 text-xs text-[#475569]">
              <Package size={11} className="text-[#94A3B8] shrink-0" />
              <span>{booking.load_size}</span>
            </div>
          )}
        </div>

        <button
          onClick={onNavigate}
          className="mt-3 w-full text-[10px] font-bold uppercase tracking-widest text-[#1B2A6B] border border-[#1B2A6B] hover:bg-[#1B2A6B] hover:text-white transition-colors py-1.5 flex items-center justify-center gap-1.5"
        >
          <ExternalLink size={10} />
          Open Confirmed Lead
        </button>
      </div>
    </div>
  );
}
